import React from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid';
import { InspoStory } from '../../../services';
import { GrowthChart } from './GrowthChart';
import { BestSellers, StatTiles } from './StoryParts';
import { firstName, namedAfterFounder, narrativeOf, stepsOf } from './storyText';
import { useInspoCta } from './InspoCta';

/** The people-photo frame, in the style guide's pale hues: Lavender Mist → Sea Breeze. */
export const BRAND_FRAME = 'bg-[linear-gradient(135deg,#f2cbfd_0%,#aaebfc_100%)]';

// #region Portrait
/**
 * The founder, big, in the wallmates.com gradient frame. Without a portrait it shows the
 * brand's own room photo, then their #1 design. Never stock imagery.
 */
const Portrait: React.FunctionComponent<{ story: InspoStory }> = ({ story }) => {
    const top = story.best_sellers.find((b) => b.image_url || b.mockup_url);
    const src = story.portrait_url ?? story.photo_urls[0] ?? top?.image_url ?? top?.mockup_url;
    if (!src) return null;
    const isPerson = !!story.portrait_url;
    const alt = isPerson ? story.founder_name ?? story.name : `${story.name}, from their website`;
    return (
        <div className={`flex h-full items-center justify-center rounded-2xl p-5 sm:p-8 ${BRAND_FRAME}`}>
            <figure className="relative w-full overflow-hidden rounded-xl shadow-xl">
                <img src={src} alt={alt} className="aspect-[4/5] w-full object-cover object-top" />
                {story.founder_name && (
                    <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 pt-10">
                        <p className="font-display text-xl font-semibold text-white">{story.founder_name}</p>
                        <p className="text-xs text-white/80">{isPerson ? `Founder, ${story.name}` : story.name}</p>
                    </figcaption>
                )}
            </figure>
        </div>
    );
};

// #region Identity
/** The name as a headline, and the wide logo on a white tile (it's drawn for white). */
const Identity: React.FunctionComponent<{ story: InspoStory; title: string }> = ({ story, title }) => (
    <div className="space-y-4">
        <h2 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight text-black sm:text-5xl dark:text-white">{title}</h2>
        <div className="flex flex-wrap items-center gap-3">
            {story.wide_logo_url && (
                <span className="inline-flex h-14 items-center rounded-xl bg-white px-3 ring-1 ring-gray-200 dark:ring-stone-600">
                    <img src={story.wide_logo_url} alt={`${story.name} logo`} className="max-h-11 w-auto max-w-[12rem] object-contain" />
                </span>
            )}
            {story.storefront_url && (
                <a href={story.storefront_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
                    {story.storefront_url.replace(/^https:\/\//, '')}
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
            )}
        </div>
    </div>
);

// #region Gallery
/** The brand's own photos (after the one already shown large). */
const Gallery: React.FunctionComponent<{ story: InspoStory }> = ({ story }) => {
    const photos = story.portrait_url ? story.photo_urls : story.photo_urls.slice(1);
    if (!photos.length) return null;
    return (
        <section className="space-y-3 border-t border-gray-200 px-5 py-6 sm:px-8 dark:border-stone-700">
            <h3 className="font-display text-xl font-semibold text-black dark:text-white">From {firstName(story) ?? story.name}&apos;s world</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {photos.map((src) => (
                    <img key={src} src={src} alt={`${story.name}, from their website`} loading="lazy"
                        className="aspect-[4/5] w-full rounded-2xl object-cover" />
                ))}
            </div>
        </section>
    );
};

// #region Quote
const Quote: React.FunctionComponent<{ story: InspoStory }> = ({ story }) => {
    if (!story.quote) return null;
    return (
        <figure className="rounded-2xl bg-lavender/40 p-5 dark:bg-lavender-deep/20">
            <blockquote className="font-display text-2xl font-semibold leading-snug text-black dark:text-white">“{story.quote}”</blockquote>
            <figcaption className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-200">{story.quote_by ?? story.founder_name ?? story.name}</figcaption>
        </figure>
    );
};

// #region Steps
const Steps: React.FunctionComponent<{ story: InspoStory }> = ({ story }) => {
    const { go } = useInspoCta();
    return (
        <ol className="grid gap-3 md:grid-cols-3">
            {stepsOf(story).map((s, i) => (
                <li key={s.title} className="flex flex-col gap-2 rounded-2xl bg-gray-50 p-5 dark:bg-stone-800/70">
                    <span className="font-display text-3xl font-semibold text-brand">0{i + 1}</span>
                    <p className="font-display text-lg font-semibold text-black dark:text-white">{s.title}</p>
                    <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">{s.body}</p>
                    {s.cta && (
                        <button type="button" onClick={() => go(s.cta?.href ?? '/', story.journey)}
                            className="mt-auto self-start text-sm font-medium text-blue-600 underline-offset-4 hover:underline dark:text-blue-400">
                            {s.cta.label} →
                        </button>
                    )}
                </li>
            ))}
        </ol>
    );
};

// #region StoryHero
/** The lead case study, written like one: who (big), what happened, the proof, and what they did. */
export const StoryHero: React.FunctionComponent<{ story: InspoStory }> = ({ story }) => {
    const featured = story.consent === 'featured';
    const name = firstName(story);
    const title = !story.founder_name ? story.name
        : namedAfterFounder(story) ? `Meet ${story.founder_name}` : `Meet ${story.founder_name} of ${story.name}`;
    return (
        <article className="overflow-hidden rounded-3xl border border-gray-200 bg-surface-alt shadow-sm dark:border-stone-700">
            <div className="grid gap-2 p-2 sm:p-3 lg:grid-cols-2">
                <Portrait story={story} />
                <div className="flex flex-col gap-6 p-4 sm:p-6 lg:p-8">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand">{story.journey === 'artist' ? 'Artist story' : 'Brand story'}</span>
                        {story.started_label && <span className="text-xs text-gray-500 dark:text-gray-400">{story.started_label}</span>}
                    </div>
                    <Identity story={story} title={title} />
                    <div>
                        <p className="font-display text-6xl font-semibold tracking-tight text-blue-600 tabular-nums sm:text-7xl dark:text-blue-400">{story.figures.monthly_now}</p>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">a month in customer sales, on average over the last 3 months</p>
                    </div>
                    <p className="text-base leading-relaxed text-gray-700 dark:text-gray-200">{narrativeOf(story)}</p>
                    <Quote story={story} />
                    <StatTiles story={story} />
                </div>
            </div>
            <section className="space-y-3 border-t border-gray-200 px-5 py-6 sm:px-8 dark:border-stone-700">
                <h3 className="font-display text-xl font-semibold text-black dark:text-white">Monthly customer sales</h3>
                <GrowthChart story={story} size="hero" />
            </section>
            {featured && (
                <section className="space-y-4 border-t border-gray-200 px-5 py-6 sm:px-8 dark:border-stone-700">
                    <h3 className="font-display text-2xl font-semibold text-black dark:text-white">What {name ?? 'they'} did</h3>
                    <Steps story={story} />
                </section>
            )}
            <Gallery story={story} />
            {story.best_sellers.length > 0 && (
                <section className="space-y-3 border-t border-gray-200 px-5 py-6 sm:px-8 dark:border-stone-700">
                    <h3 className="font-display text-xl font-semibold text-black dark:text-white">What sells for {name ?? 'them'}</h3>
                    <BestSellers items={story.best_sellers} />
                </section>
            )}
        </article>
    );
};
